import { Fragment } from 'react';
import { Link } from 'react-router-dom';

interface Props {
  text: string;
  className?: string;
}

// Matches "#NNN" where the # is at start-of-string or preceded by whitespace
// or common punctuation. Avoids matching mid-word like "abc#123".
const RE = /(^|[\s(\[{,;])#(\d+)\b/g;

/**
 * Render text with any "#NNN" patterns turned into Links to /tickets/NNN.
 * Preserves whitespace via white-space: pre-wrap on the wrapping element.
 * Use anywhere we render ticket descriptions, comments, or resolution text.
 */
export function TextWithTicketLinks({ text, className }: Props) {
  if (!text) return <span className={className}></span>;

  const parts: { node: React.ReactNode; key: string }[] = [];
  let lastIdx = 0;
  let m: RegExpExecArray | null;
  let segIdx = 0;
  // Reset regex state (it's stateful when /g).
  RE.lastIndex = 0;
  while ((m = RE.exec(text)) !== null) {
    const [match, prefix, idStr] = m;
    const start = m.index + (prefix ? prefix.length : 0);
    const end = m.index + match.length;
    if (start > lastIdx) {
      parts.push({
        node: text.slice(lastIdx, start),
        key: `t${segIdx++}`,
      });
    }
    parts.push({
      node: (
        <Link
          to={`/tickets/${idStr}`}
          className="text-brand-600 hover:text-brand-700 hover:underline"
        >
          #{idStr}
        </Link>
      ),
      key: `l${segIdx++}-${idStr}`,
    });
    lastIdx = end;
  }
  if (lastIdx < text.length) {
    parts.push({ node: text.slice(lastIdx), key: `t${segIdx++}` });
  }

  return (
    <span className={className}>
      {parts.map((p) => (
        <Fragment key={p.key}>{p.node}</Fragment>
      ))}
    </span>
  );
}
